import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency } from '../../utils/formatters.js';

export function DownPaymentApplyModal({ downPayment, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [invoiceId, setInvoiceId] = useState('');
  const [error, setError] = useState(null);

  const open = Boolean(downPayment);
  // Only AR down payments clear against sales invoices; vendor ones against purchase invoices.
  const invoiceType = downPayment?.partyType === 'VENDOR' ? 'PURCHASE' : 'SALES';
  const invoices = useQuery({
    queryKey: ['finance', 'invoices', 'posted', invoiceType],
    queryFn: () => financeApi.listInvoices({ type: invoiceType, status: 'POSTED' }).then((r) => r.items),
    enabled: open,
  });

  useEffect(() => { if (open) { setInvoiceId(''); setError(null); } }, [open, downPayment?.id]);

  const apply = useMutation({
    mutationFn: () => financeApi.applyDownPayment(downPayment.id, { invoiceId }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance', 'down-payments'] }); onClose(); },
    onError: (e) => setError(e?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!invoiceId) { setError(t('dp.error.invoiceRequired')); return; }
    apply.mutate();
  }

  const open0 = (inv) => Number(inv.totalAmount) - Number(inv.paidAmount);

  return (
    <Modal open={open} onClose={onClose} title={downPayment ? `${t('dp.apply.title')} · ${downPayment.dpNumber}` : t('dp.apply.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={apply.isPending || !invoiceId}>{apply.isPending ? `${t('dp.action.apply')}…` : t('dp.action.apply')}</Button></>}>
      {downPayment && (
        <form onSubmit={submit} className="form-stack">
          <p className="muted text-sm">{t('dp.apply.hint', { amount: formatCurrency(downPayment.amount), party: downPayment.partyName })}</p>
          <label className="field">
            <span className="field-label">{t('dp.apply.invoice')}</span>
            <select className="input" value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} required>
              <option value="">-</option>
              {(invoices.data || []).map((inv) => (
                <option key={inv.id} value={inv.id}>{inv.invoiceNumber} · {formatCurrency(inv.totalAmount)} ({t('dp.apply.open')}: {formatCurrency(open0(inv))})</option>
              ))}
            </select>
          </label>
          {(invoices.data || []).length === 0 && !invoices.isLoading && <div className="alert">{t('dp.apply.noInvoices')}</div>}
          {error && <div className="alert danger">{error}</div>}
        </form>
      )}
    </Modal>
  );
}
